import { applySortModel } from './filterUtils';

/**
 * Normalize common API list payloads into { results, total }.
 */
export function normalizeServerSidePage(response) {
  if (response == null) {
    return { results: [], total: 0 };
  }
  if (Array.isArray(response)) {
    return { results: response, total: response.length };
  }

  // Explicit failure payloads
  if (response.error && !response.results && !response.data && !response.allVendorsDetails) {
    return { error: response };
  }
  // Express handlers often res.send(JSON.stringify({ message, stack }))
  if (
    response.message &&
    response.stack &&
    !response.results &&
    !response.Results &&
    !response.data &&
    !response.rows
  ) {
    return { error: response };
  }

  const nested = response.data && typeof response.data === 'object' ? response.data : null;

  const results =
    response.results ||
    response.Results ||
    response.rows ||
    response.allVendorsDetails ||
    (nested && (nested.results || nested.Results || nested.rows || nested.allVendorsDetails)) ||
    (Array.isArray(response.data) ? response.data : null) ||
    [];

  const totalRaw =
    response.total != null
      ? response.total
      : response.Total != null
        ? response.Total
      : nested && nested.total != null
        ? nested.total
        : nested && nested.Total != null
          ? nested.Total
        : Array.isArray(results)
          ? results.length
          : 0;

  return {
    results: Array.isArray(results) ? results : [],
    total: Number(totalRaw) || 0,
  };
}

/**
 * Creates an AG Grid server-side datasource.
 *
 * @param {object} options
 * @param {function} options.fetchPage - async (body) => { results, total } or raw API JSON
 * @param {object} options.filterBody - mutable request body (pageNo, pageSize, sort*, searchObject)
 * @param {function} [options.mapFilterModel] - (filterModel) => searchObject
 * @param {object} [options.sortMap] - colId -> { tableName, sortColumn }
 * @param {boolean} [options.pageZeroBased=true] - pharmacy APIs use 0-based pageNo
 * @param {function} [options.onSuccess] - (results, total) => void
 * @param {function} [options.onError] - (error) => void
 * @param {function} [options.getPageSize] - () => number (defaults to filterBody.pageSize)
 */
export const createServerSideDatasource = ({
  fetchPage,
  filterBody,
  mapFilterModel,
  applyFilterModel,
  sortMap,
  pageZeroBased = true,
  onSuccess,
  onError,
  getPageSize,
}) => {
  let requestSeq = 0;

  return {
    getRows(params) {
      const mySeq = ++requestSeq;
      const pageSize =
        (getPageSize && getPageSize()) ||
        filterBody.pageSize ||
        (params.request.endRow - params.request.startRow) ||
        10;

      filterBody.pageSize = pageSize;

      if (pageZeroBased) {
        filterBody.pageNo = Math.floor(params.request.startRow / pageSize);
      } else {
        filterBody.pageNo = Math.floor(params.request.startRow / pageSize) + 1;
      }

      const filterModel = params.request && params.request.filterModel;
      if (applyFilterModel) {
        applyFilterModel(filterBody, filterModel || {});
      } else if (mapFilterModel) {
        const mapped = mapFilterModel(filterModel || {});
        if (mapped === '' || mapped == null) {
          delete filterBody.searchObject;
        } else {
          filterBody.searchObject = mapped;
        }
      } else if (filterModel && Object.keys(filterModel).length > 0) {
        filterBody.searchObject = filterModel;
      } else {
        delete filterBody.searchObject;
      }

      // Match pre-migration payloads: omit empty/null searchObject & group_id
      if (
        filterBody.searchObject == null ||
        filterBody.searchObject === '' ||
        (typeof filterBody.searchObject === 'object' &&
          !Array.isArray(filterBody.searchObject) &&
          Object.keys(filterBody.searchObject).length === 0)
      ) {
        delete filterBody.searchObject;
      }
      if (
        filterBody.group_id == null ||
        filterBody.group_id === '' ||
        filterBody.group_id === 'null' ||
        filterBody.group_id === 'undefined'
      ) {
        delete filterBody.group_id;
      }

      if (params.request.sortModel && params.request.sortModel.length > 0 && sortMap) {
        applySortModel(filterBody, params.request.sortModel, sortMap);
      }

      // Clone so internal flags never hit the API.
      const requestBody = { ...filterBody };
      delete requestBody.__dateFilterBound;
      delete requestBody.__dateFilterNeedsReseed;
      delete requestBody.__dateFilterColId;

      const FETCH_TIMEOUT_MS = 90000;
      let timeoutId = null;
      const timeoutPromise = new Promise((_, reject) => {
        timeoutId = setTimeout(
          () => reject(new Error('Report request timed out')),
          FETCH_TIMEOUT_MS
        );
      });

      Promise.race([fetchPage(requestBody), timeoutPromise])
        .then((response) => {
          if (timeoutId) clearTimeout(timeoutId);
          // Some Express error paths send a JSON string body.
          if (typeof response === 'string') {
            try {
              response = JSON.parse(response);
            } catch (e) {
              response = { message: response, stack: '' };
            }
          }
          const normalized = normalizeServerSidePage(response);

          if (normalized.error) {
            if (mySeq === requestSeq && onError) onError(normalized.error);
            if (typeof params.fail === 'function') {
              params.fail();
            } else if (typeof params.failCallback === 'function') {
              params.failCallback();
            }
            return;
          }

          const results = normalized.results;
          let total = normalized.total;

          // If API omits total but returned a full page, keep the store open for more pages.
          if ((!total || total < results.length) && results.length > 0) {
            const startRow =
              (params.request && params.request.startRow) != null
                ? params.request.startRow
                : 0;
            if (results.length >= pageSize) {
              total = startRow + results.length + 1;
            } else {
              total = startRow + results.length;
            }
          }

          // Stamp unique ids — duplicate getRowId values leave AG Grid 29 SSRM
          // stuck on the loading cell even after a successful API response.
          const startRow =
            (params.request && params.request.startRow) != null
              ? params.request.startRow
              : 0;
          const rowData = (results || []).map((row, index) => {
            if (!row || typeof row !== 'object') return row;
            if (row.__ssrmId) return row;
            return {
              ...row,
              __ssrmId: [
                startRow + index,
                row.prescription_txn_id,
                row.order_id,
                row.stock_code,
                row.binid,
                row.rxno,
                row.fillid,
                row.stockeddate,
                row.createdon,
                row.id,
                row.pmid,
              ]
                .filter((v) => v != null && String(v).trim() !== '')
                .join('|'),
            };
          });

          // AG Grid 29 prefers success(); successCallback is a deprecated wrapper.
          let notifiedGrid = false;
          try {
            if (typeof params.success === 'function') {
              params.success({
                rowData,
                rowCount: total,
              });
              notifiedGrid = true;
            } else if (typeof params.successCallback === 'function') {
              params.successCallback(rowData, total);
              notifiedGrid = true;
            }
          } catch (e) {
            console.error(e);
            if (!notifiedGrid && typeof params.successCallback === 'function') {
              try {
                params.successCallback(rowData, total);
                notifiedGrid = true;
              } catch (e2) {
                console.error(e2);
              }
            }
          }
          if (!notifiedGrid) {
            console.error('AG Grid SSRM: no success callback available');
          }

          // Only the latest request updates parent React state / overlays.
          if (mySeq !== requestSeq) return;

          if (onSuccess) onSuccess(rowData, total);

          if (params.api) {
            if (!total || total === 0 || !rowData.length) {
              params.api.showNoRowsOverlay();
            } else {
              params.api.hideOverlay();
            }
          }
        })
        .catch((error) => {
          if (timeoutId) clearTimeout(timeoutId);
          console.error(error);
          if (mySeq === requestSeq && onError) onError(error);
          if (params.api && typeof params.api.hideOverlay === 'function') {
            params.api.hideOverlay();
          }
          if (typeof params.fail === 'function') {
            params.fail();
          } else if (typeof params.failCallback === 'function') {
            params.failCallback();
          }
        });
    },
  };
};

export default createServerSideDatasource;



// WEBPACK FOOTER //
// ./src/components/grid/createServerSideDatasource.js